import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Footprints, Upload, UserPlus, ScanLine, AlertTriangle,
  CheckCircle2, XCircle, Loader2, WifiOff, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { ASSETS } from "@/data/content";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import {
  enrollSubject, listGallery, analyzeVideo, getJob, jobVideoUrl, getHealth,
} from "@/lib/api";

const DONE = ["done", "error"];
// El polling se detiene cuando el job termina (done/error), si no cada 1.5s.
const pollOpts = (jobId) => ({
  queryKey: ["job", jobId],
  queryFn: () => getJob(jobId),
  enabled: !!jobId,
  refetchInterval: (q) => (DONE.includes(q.state.data?.status) ? false : 1500),
});

// Sonda de vida del backend. El motor corre en una maquina local expuesta a
// internet por un tunel de Cloudflare: si esa maquina se apaga, se reinicia o
// pierde la red, la pagina tiene que DECIRLO. Antes no lo hacia: la galeria
// mostraba "no hay sujetos" y un enlace /analizar?job=<id> se veia en blanco,
// que es exactamente lo que no puede pasar frente al cliente.
const SALUD_MS = 15000;

function useSalud() {
  const q = useQuery({
    queryKey: ["health"],
    queryFn: getHealth,
    refetchInterval: SALUD_MS,
    // Un solo reintento: queremos enterarnos rapido de la caida, no insistir.
    retry: 1,
  });
  return { caido: q.isError, motor: q.data?.runner };
}

function AvisoSinBackend() {
  return (
    <div className="px-4 lg:px-6 py-3 bg-red-500/10 border-b border-red-500/30"
      data-testid="backend-offline-banner">
      <p className="flex items-start gap-2 text-[12px] text-red-300">
        <WifiOff className="h-4 w-4 shrink-0 mt-0.5" />
        <span>
          <strong className="font-semibold">Sin conexión con el motor de análisis.</strong>{" "}
          La computadora que procesa los videos no está respondiendo. Mientras tanto no se
          puede enrolar ni analizar, y los análisis ya procesados no se pueden abrir.
          Se reintenta la conexión cada 15 segundos.
        </span>
      </p>
    </div>
  );
}

// El id del analisis vive en la URL (/analizar?job=<id>). Sirve para dos cosas:
// abrir un analisis YA procesado sin volver a correrlo, y no perder el que esta
// en curso si se recarga la pagina. Es lo que hace posible el demo: cada corrida
// tarda entre 12 y 27 minutos, asi que no se puede procesar en vivo.
const jobDeLaUrl = () => new URLSearchParams(window.location.search).get("job");

function recordarEnUrl(id) {
  const url = new URL(window.location.href);
  if (id) url.searchParams.set("job", id);
  else url.searchParams.delete("job");
  window.history.replaceState({}, "", url);
}

function formatoDuracion(segundos) {
  const m = Math.floor(segundos / 60);
  const s = Math.floor(segundos % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Cronometro del analisis. La barra avanza en saltos grandes (se queda en 40%
// durante la mayor parte de la corrida), asi que sin un tiempo corriendo la
// pantalla parece congelada.
function Transcurrido({ job }) {
  const [ahora, setAhora] = useState(() => Date.now());
  const terminado = DONE.includes(job?.status);

  useEffect(() => {
    if (terminado) return;
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [terminado]);

  if (!job?.created_at) return null;
  const inicio = new Date(job.created_at).getTime();
  const fin = terminado && job.updated_at ? new Date(job.updated_at).getTime() : ahora;
  const segundos = Math.max(0, (fin - inicio) / 1000);

  return (
    <span className="font-mono text-xs text-slate-400" data-testid="job-elapsed">
      {terminado ? "tardó " : ""}{formatoDuracion(segundos)}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    queued: ["Encolado", "text-slate-300 bg-slate-700/40 border-slate-600"],
    running: ["Procesando", "text-blue-300 bg-blue-500/10 border-blue-500/30"],
    done: ["Listo", "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"],
    error: ["Error", "text-red-300 bg-red-500/10 border-red-500/30"],
  };
  const [label, cls] = map[status] || map.queued;
  return <span className={`px-2 py-0.5 rounded border text-[11px] font-mono ${cls}`}>{label}</span>;
}

// Un enlace /analizar?job=<id> que no carga es el peor escenario del demo: son
// los resultados pre-procesados que se abren frente al cliente. Antes esto se
// veia como una pantalla vacia; ahora dice que pasa y deja reintentar.
function JobNoCarga({ jobId, onReintentar, reintentando }) {
  return (
    <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/5 p-4"
      data-testid="job-load-error">
      <p className="flex items-start gap-2 text-sm text-red-300">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
        <span>
          <strong className="font-semibold">No se pudo abrir este análisis.</strong>{" "}
          El resultado sigue guardado en la computadora que procesa los videos; lo que
          falla es la conexión con ella, no el análisis.
        </span>
      </p>
      <p className="mt-2 font-mono text-[11px] text-slate-500 break-all">id: {jobId}</p>
      <Button variant="outline" size="sm" className="mt-3" data-testid="job-retry-btn"
        disabled={reintentando} onClick={onReintentar}>
        {reintentando
          ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Reintentando…</>
          : <><RefreshCw className="h-3.5 w-3.5 mr-2" /> Reintentar</>}
      </Button>
    </div>
  );
}

function JobProgress({ job }) {
  if (!job) return null;
  return (
    <div className="mt-4 rounded-lg border border-slate-800 bg-slate-900/60 p-4" data-testid="job-progress">
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-2 text-sm text-slate-300">
          {!DONE.includes(job.status) && <Loader2 className="h-4 w-4 animate-spin text-blue-400" />}
          <span className="font-mono text-xs text-slate-400">etapa: {job.stage}</span>
          <Transcurrido job={job} />
        </span>
        <StatusBadge status={job.status} />
      </div>
      <Progress value={job.progress ?? 0} />
      {!DONE.includes(job.status) && (
        <p className="mt-2 text-[11px] text-slate-500">
          El análisis corre en GPU y tarda varios minutos. La barra avanza por etapas,
          no de forma continua: es normal que se quede fija un buen rato.
        </p>
      )}
      {job.error && <p className="mt-2 text-xs text-red-400">{job.error}</p>}
    </div>
  );
}

// --- Enrolar sujeto ---
function EnrollTab() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [jobId, setJobId] = useState(null);

  const gallery = useQuery({ queryKey: ["gallery"], queryFn: listGallery });
  const job = useQuery(pollOpts(jobId));
  const { caido } = useSalud();

  const enroll = useMutation({
    mutationFn: () => enrollSubject(name, file),
    onSuccess: (d) => setJobId(d.job_id),
    onError: (e) => toast.error("No se pudo enrolar: " + (e?.message || "error")),
  });

  // Al terminar el enrolamiento: avisar e invalidar la galería (en efecto, NO en render).
  useEffect(() => {
    const s = job.data?.status;
    if (!jobId || !s || !DONE.includes(s)) return;
    if (s === "done") {
      toast.success(`Sujeto "${job.data.subject_name}" enrolado`);
      qc.invalidateQueries({ queryKey: ["gallery"] });
    } else {
      toast.error("El enrolamiento falló: " + (job.data.error || ""));
    }
    setJobId(null);
  }, [job.data?.status, job.data?.subject_name, job.data?.error, jobId, qc]);

  const busy = enroll.isPending || (!!jobId && !DONE.includes(job.data?.status));

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
        <h3 className="flex items-center gap-2 font-display font-semibold text-white mb-4">
          <UserPlus className="h-4 w-4 text-blue-400" /> Enrolar sujeto de referencia
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="subj-name">Nombre</Label>
            <Input id="subj-name" data-testid="enroll-name" placeholder="Ej. Ana"
              value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="subj-file">Video (perfil, cuerpo completo)</Label>
            <Input id="subj-file" data-testid="enroll-file" type="file" accept="video/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
        </div>
        <Button className="mt-4" data-testid="enroll-submit" disabled={busy || !name.trim() || !file || caido}
          onClick={() => enroll.mutate()}>
          {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Procesando…</> : <><Upload className="h-4 w-4 mr-2" /> Enrolar</>}
        </Button>
        {jobId && <JobProgress job={job.data} />}
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2">
          <ScanLine className="h-4 w-4 text-blue-400" />
          <span className="font-display font-semibold text-sm text-white">Galería enrolada</span>
        </div>
        {gallery.data?.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Frames</TableHead>
                <TableHead>Enrolado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="gallery-list">
              {gallery.data.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="text-slate-200">{s.name}</TableCell>
                  <TableCell className="font-mono text-slate-400">{s.frames}</TableCell>
                  <TableCell className="font-mono text-[11px] text-slate-500">
                    {new Date(s.created_at).toLocaleString("es-MX")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : gallery.isError ? (
          // Distinto de "no hay sujetos": aqui no pudimos preguntar. Decir
          // "no hay enrolados" cuando el backend esta caido hace creer que se
          // perdieron los datos.
          <p className="px-4 py-6 text-sm text-red-300 flex items-start gap-2" data-testid="gallery-error">
            <WifiOff className="h-4 w-4 shrink-0 mt-0.5" />
            No se pudo consultar la galería. Los sujetos enrolados siguen guardados;
            lo que falla es la conexión con la computadora que los tiene.
          </p>
        ) : (
          <p className="px-4 py-6 text-sm text-slate-500">Aún no hay sujetos enrolados.</p>
        )}
      </div>
    </div>
  );
}

// --- Analizar video ---
function AnalyzeTab() {
  const [file, setFile] = useState(null);
  // Arranca con el id que venga en la URL, para poder abrir un analisis ya
  // procesado (o recuperar el que estaba corriendo tras recargar).
  const [jobId, setJobId] = useState(jobDeLaUrl);
  const { caido } = useSalud();

  const job = useQuery(pollOpts(jobId));
  const analyze = useMutation({
    mutationFn: () => analyzeVideo(file),
    onSuccess: (d) => {
      setJobId(d.job_id);
      recordarEnUrl(d.job_id);
    },
    onError: (e) => toast.error("No se pudo analizar: " + (e?.message || "error")),
  });

  const j = job.data;
  const busy = analyze.isPending || (!!jobId && !DONE.includes(j?.status));
  const done = j && j.status === "done";

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
        <h3 className="flex items-center gap-2 font-display font-semibold text-white mb-4">
          <Footprints className="h-4 w-4 text-blue-400" /> Analizar video
        </h3>
        <div className="space-y-1.5 max-w-md">
          <Label htmlFor="probe-file">Video a analizar</Label>
          <Input id="probe-file" data-testid="analyze-file" type="file" accept="video/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </div>
        <Button className="mt-4" data-testid="analyze-submit" disabled={busy || !file || caido}
          onClick={() => { setJobId(null); recordarEnUrl(null); analyze.mutate(); }}>
          {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Procesando…</> : <><Upload className="h-4 w-4 mr-2" /> Analizar</>}
        </Button>
        {caido && (
          <p className="mt-2 text-[11px] text-red-400/80">
            Deshabilitado mientras no haya conexión con el motor.
          </p>
        )}
        {jobId && (job.isError
          ? <JobNoCarga jobId={jobId} reintentando={job.isFetching}
              onReintentar={() => job.refetch()} />
          : <JobProgress job={j} />)}
      </div>

      {done && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden" data-testid="analyze-result">
          <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
            <span className="font-display font-semibold text-sm text-white">Resultado</span>
            <span className="font-mono text-[11px] text-slate-500">
              {j.stats?.personas ?? j.matches?.length ?? 0} persona(s) · {j.stats?.matches ?? 0} coincidencia(s)
            </span>
          </div>

          {j.has_annotated_video && (
            <video data-testid="annotated-video" src={jobVideoUrl(jobId)} controls
              className="w-full max-h-[420px] bg-black" />
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Track</TableHead>
                <TableHead>Frames</TableHead>
                <TableHead>Coincidencia</TableHead>
                <TableHead>Distancia</TableHead>
                <TableHead>Confianza</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="matches-list">
              {(j.matches || []).map((m) => (
                <TableRow key={m.track_id}>
                  <TableCell className="font-mono text-xs text-slate-300">{m.track_id}</TableCell>
                  <TableCell className="font-mono text-slate-400">{m.frames}</TableCell>
                  <TableCell>
                    {m.matched ? (
                      <span className="flex items-center gap-1.5 text-emerald-400 text-sm">
                        <CheckCircle2 className="h-4 w-4" /> {m.subject_name}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-slate-500 text-sm">
                        <XCircle className="h-4 w-4" /> Sin coincidencia
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-slate-400">{m.distance ?? "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 rounded-full bg-slate-800 overflow-hidden">
                        <div className={`h-full rounded-full ${m.confidence >= 85 ? "bg-emerald-400" : m.confidence >= 75 ? "bg-blue-400" : "bg-slate-500"}`}
                          style={{ width: `${m.confidence}%` }} />
                      </div>
                      <span className="font-mono text-xs text-slate-400 w-9 text-right">{m.confidence}%</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function AnalizarVideo() {
  const { caido } = useSalud();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur">
        <div className="px-4 lg:px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/" data-testid="analyze-back-btn" className="flex items-center gap-1.5 text-slate-400 hover:text-white text-sm shrink-0">
              <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Volver</span>
            </Link>
            <div className="h-5 w-px bg-slate-700" />
            <img src={ASSETS.logo} alt="" className="h-6 w-6 object-contain" />
            <span className="font-display font-bold text-white truncate">Analizar Video · GAIT</span>
          </div>
        </div>
      </header>

      {caido && <AvisoSinBackend />}

      {/* Aviso de modo demostración (ticket 7) */}
      <div className="px-4 lg:px-6 py-2 bg-amber-500/10 border-b border-amber-500/30">
        <p className="flex items-center gap-2 text-[12px] text-amber-300 font-mono">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Motor en modo demostración (OpenGait, uso académico) — no apto para producción.
        </p>
      </div>

      <main className="flex-1 p-4 lg:p-6 max-w-5xl w-full mx-auto">
        <Tabs defaultValue="analizar">
          <TabsList>
            <TabsTrigger value="analizar" data-testid="tab-analizar">Analizar video</TabsTrigger>
            <TabsTrigger value="enrolar" data-testid="tab-enrolar">Enrolar sujeto</TabsTrigger>
          </TabsList>
          <TabsContent value="analizar" className="mt-6"><AnalyzeTab /></TabsContent>
          <TabsContent value="enrolar" className="mt-6"><EnrollTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
